/**
 * AnimalScene - Animal friends mini-game
 */

import { useState, useEffect } from 'react';

const ANIMALS = [
  { emoji: '🐕', name: 'dog', sound: 'woof' },
  { emoji: '🐈', name: 'cat', sound: 'meow' },
  { emoji: '🐰', name: 'bunny', sound: 'hop' },
  { emoji: '🐦', name: 'bird', sound: 'tweet' },
];

export default function AnimalScene({ level, isComplete, isListening }) {
  const [activeAnimal, setActiveAnimal] = useState(0);
  const [showHearts, setShowHearts] = useState(false);

  useEffect(() => {
    const config = level?.sceneConfig || {};
    const animalIdx = ANIMALS.findIndex(a => a.name === config.animal);
    setActiveAnimal(animalIdx >= 0 ? animalIdx : 0);
    setShowHearts(false);
  }, [level?.id]);

  useEffect(() => {
    if (isComplete) {
      setShowHearts(true);
    }
  }, [isComplete]);

  const animal = ANIMALS[activeAnimal];

  return (
    <div style={styles.scene}>
      {/* Meadow background */}
      <div style={styles.meadow}>
        <div style={styles.sky} />
        <div style={styles.grass} />
        {/* Flowers */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.flower,
              left: `${10 + i * 12}%`,
              bottom: `${15 + (i % 3) * 3}%`,
            }}
          >
            {['🌸', '🌼', '🌺'][i % 3]}
          </div>
        ))}
      </div>

      {/* Animal */}
      <div style={styles.animalContainer}>
        <div style={styles.animal}>
          {animal.emoji}
        </div>
        {/* Hearts */}
        {showHearts && (
          <div style={styles.hearts}>
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                style={{
                  ...styles.heart,
                  animationDelay: `${i * 0.1}s`,
                  left: `${30 + i * 10}%`,
                }}
              >
                ❤️
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status */}
      <div style={styles.status}>
        {isComplete 
          ? `The ${animal.name} is happy!`
          : level?.sceneConfig?.question
            ? `What does the ${animal.name} say?`
            : `Talk about the ${animal.name}!`
        }
      </div>
    </div>
  );
}

const styles = {
  scene: { position: 'absolute', inset: 0, overflow: 'hidden' },
  meadow: { position: 'absolute', inset: 0 },
  sky: { position: 'absolute', top: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(180deg, #38bdf8 0%, #7dd3fc 100%)' },
  grass: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(180deg, #4ade80 0%, #22c55e 100%)' },
  flower: { position: 'absolute', fontSize: '30px', animation: 'flowerSway 3s ease-in-out infinite' },
  animalContainer: { position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' },
  animal: { fontSize: '100px', animation: 'animalBounce 2s ease-in-out infinite', filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.2))' },
  hearts: { position: 'absolute', top: '-50px', left: '50%', transform: 'translateX(-50%)', width: '150px', height: '100px' },
  heart: { position: 'absolute', fontSize: '30px', animation: 'heartFloat 1s ease-out forwards' },
  status: { position: 'absolute', bottom: '15%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '12px 24px', borderRadius: '30px', fontFamily: "'Fredoka', sans-serif", fontSize: '16px', fontWeight: 500, textAlign: 'center', maxWidth: '80%' },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes animalBounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
  @keyframes flowerSway { 0%, 100% { transform: rotate(-5deg); } 50% { transform: rotate(5deg); } }
  @keyframes heartFloat { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-50px); } }
`;
if (!document.querySelector('#animal-scene-styles')) {
  styleSheet.id = 'animal-scene-styles';
  document.head.appendChild(styleSheet);
}
